import React, {useEffect, useRef, useState} from 'react';
import {classes, style} from './doppe-viewer-thank-you-modal.st.css';
import type {OmitStrict} from '@wix/devzai-utils-common';
import {AsyncValueStates, iterableMapToArray} from '@wix/devzai-utils-common';
import {
    flexColumn,
    flexFillRemainingVerticalSpace,
    reactCreateMemo,
    useAsyncOperation,
    useEventCallback,
} from '@wix/devzai-utils-react';
import {DoppeViewerContentModal} from '../doppe-viewer-content-modal/doppe-viewer-content-modal';
import {DoppeViewerModalPanelLayout} from '../doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services-react';
import type {
    DoppeDtoThankYouModalData,
    DoppeDtoThankYouModalLineItem,
} from '../../client-server-common';
import {
    DoppeViewerHorizontalButton,
    DoppeViewerHorizontalButtonPriority,
    DoppeViewerHorizontalButtonSkin,
} from '../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button';
import {DoppeViewerScrollView} from '../doppe-viewer-scroll-view/doppe-viewer-scroll-view';
import {DoppeViewerSpinner, DoppeViewerSpinnerSize} from '../doppe-viewer-spinner/doppe-viewer-spinner';

/**
 * Native post-checkout "thank you" modal — the universal post-purchase UX
 * for every paid Doppe flow (Buy Now, Cart, custom-line-item payment
 * actions, Payment Lock, Paid Download File CTA, Paid Request, Paid DM,
 * Donate / Tip Jar).
 *
 * **Lifecycle.** The modal owns its own data fetch. The parent mounts it
 * with `checkoutId` + `pageId` (extracted from the post-checkout redirect)
 * and an `onFetchFailure` callback; the modal then calls
 * `getWixEcomOrderThankYouData` on mount and renders:
 *
 *  - A centered spinner while the order is being resolved (server-side
 *    retry budget is ≈ 15s, sized to cover the slower-propagating
 *    custom-line-item orders for Paid Request / Paid DM / Donate).
 *  - The full order summary once the data resolves.
 *  - Nothing (and a single `onFetchFailure()` invocation) when the fetch
 *    rejects or the server reports a non-success status; the parent is
 *    responsible for swapping to the legacy iframe modal as a last-resort
 *    fallback.
 *
 * The modal stays open and dismissable via the header X button at every
 * phase. ESC + outside-click are blocked by the controller's
 * `preventAutoClose: true` configuration on the parent's `showInnerModal`
 * call — dismissal must be an intentional buyer action.
 *
 * **Auto-unlock on first success.** For every line item that carries a
 * `lockedEntityId` (extracted from the order's `Reference` description
 * line), the modal flips the matching action / page lock to "unlocked"
 * using the existing cookie + observable primitives in
 * `DoppeViewerAppLockService`. By the time the buyer dismisses the modal,
 * the locked surface is already accessible. No buyer-supplied credentials
 * are required — `checkoutId` is the proof of payment. The effect fires
 * exactly once when data first transitions to Resolved.
 *
 * **Per-line-item Download button.** Shown only for line items the server
 * flagged as `hasDownloadableAsset` — Wix Stores DIGITAL products with a
 * bound `digitalFile.fileId` and Doppe-custom Paid Download File CTAs whose
 * action on the current bymo page carries a bound media file. Payment Lock
 * / Paid Request / Paid DM / Donate line items (no downloadable asset)
 * never surface a Download button.
 */
export const DoppeViewerThankYouModal = reactCreateMemo(
    function DoppeViewerThankYouModal(props: DoppeViewerThankYouModal.Props) {
        const {checkoutId, pageId, closeCallback, onFetchFailure, ...restProps} = props;

        const viewerLocales = useDoppeViewerLocales();
        const clientServices = useDoppeViewerClientServices();

        const [operation, setOperation] = useAsyncOperation<DoppeDtoThankYouModalData>();

        // Mount-only fetch. The modal is constructed fresh per post-checkout
        // redirect, so a single fire-and-forget kick-off is correct. Reading
        // checkoutId / pageId / clientServices at mount is intentional.
        useEffect(() => {
            setOperation((async () => {
                const result = await clientServices.getApiClient().getWixEcomOrderThankYouData({
                    checkoutId: checkoutId,
                    pageId: pageId,
                });

                // Collapse "server returned non-success" into a rejection so
                // the operation state has a clean Resolved / Rejected
                // discriminant — Resolved always means "valid thankYouData",
                // Rejected always means "fall back to iframe".
                if (result.status !== 'success') {
                    throw new Error(`getWixEcomOrderThankYouData failure: ${result.reason}`);
                }

                return result.thankYouData;
            })());
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Single-fire failure propagation. Use a ref so a parent re-render
        // that swaps in a new `onFetchFailure` reference doesn't re-trigger.
        const stableOnFetchFailure = useEventCallback(onFetchFailure);
        const failureFiredRef = useRef(false);
        useEffect(() => {
            if (operation?.state === AsyncValueStates.Rejected && !failureFiredRef.current) {
                failureFiredRef.current = true;
                stableOnFetchFailure();
            }
        }, [operation, stableOnFetchFailure]);

        // Single-fire auto-unlock on first successful resolution.
        const unlockAppliedRef = useRef(false);
        useEffect(() => {
            if (operation?.state !== AsyncValueStates.Resolved || unlockAppliedRef.current) {
                return;
            }

            unlockAppliedRef.current = true;

            const thankYouData = operation.value;
            const lockedEntityIds = iterableMapToArray(
                thankYouData.lineItems,
                (lineItem, skip) => lineItem.lockedEntityId ?? skip,
            );

            if (lockedEntityIds.length === 0) return;

            const unlockMetadata = {
                orderNumber: thankYouData.orderNumber,
                email: thankYouData.buyerEmail,
            };

            const lockService = clientServices.lockService;

            for (const lockedEntityId of lockedEntityIds) {
                lockService.applyUnlockedActionStateById(lockedEntityId, unlockMetadata);
                lockService.applyUnlockedPageStateById(lockedEntityId, unlockMetadata);
            }
        }, [operation, clientServices]);

        const resolvedThankYouData = operation?.state === AsyncValueStates.Resolved
            ? operation.value
            : null;

        const title = viewerLocales['thankYouModal.title'];
        const subtitle = resolvedThankYouData !== null
            ? viewerLocales['thankYouModal.subtitle']({orderNumber: resolvedThankYouData.orderNumber})
            : null;

        return (
            <DoppeViewerContentModal
                mobileLayout={DoppeViewerModalPanelLayout.BottomDrawer}
                desktopLayout={DoppeViewerModalPanelLayout.Inner}
                showHeader={true}
                showHeaderAsCloseButton={true}
                title={title}
                {...restProps}
                closeCallback={closeCallback}
                content={() => (
                    <div className={style(classes.root, {}, flexColumn())}>
                        <div className={classes.header}>
                            <h2 className={classes.title}>{title}</h2>
                            {subtitle !== null ?
                                <p className={classes.subtitle}>{subtitle}</p>
                                : null
                            }
                        </div>

                        {resolvedThankYouData !== null ?
                            <DoppeViewerScrollView
                                className={style(classes.scrollableContent, {}, flexFillRemainingVerticalSpace())}
                            >
                                <div className={style(classes.scrollableInner, {}, flexColumn())}>
                                    {resolvedThankYouData.lineItems.length > 0 ?
                                        <section className={classes.section}>
                                            <h3 className={classes.sectionTitle}>
                                                {viewerLocales['thankYouModal.section.items']}
                                            </h3>
                                            <ul className={classes.lineItemsList}>
                                                {resolvedThankYouData.lineItems.map(lineItem => (
                                                    <DoppeViewerThankYouModalLineItemRow
                                                        key={lineItem.id}
                                                        lineItem={lineItem}
                                                        checkoutId={checkoutId}
                                                        pageId={pageId}
                                                    />
                                                ))}
                                            </ul>
                                        </section>
                                        : null
                                    }

                                    <DoppeViewerThankYouModalPriceSummary thankYouData={resolvedThankYouData}/>
                                </div>
                            </DoppeViewerScrollView>
                            :
                            <div className={style(classes.loadingContainer, {}, flexFillRemainingVerticalSpace())}>
                                <DoppeViewerSpinner size={DoppeViewerSpinnerSize.Medium}/>
                            </div>
                        }
                    </div>
                )}
            />
        );
    }
);

export namespace DoppeViewerThankYouModal {
    export interface Props extends OmitStrict<DoppeViewerContentModal.Props, 'content' | 'title' | 'closeCallback'> {
        /**
         * Checkout id extracted from the post-checkout `iframeModalURL`
         * redirect. Used to resolve the order on mount via
         * `getWixEcomOrderThankYouData` and forwarded to
         * `generateOrderLineItemDownloadUrl` when the buyer clicks a
         * Download button.
         */
        checkoutId: string;
        /**
         * Bymo page id the modal opens over. Scopes the Doppe-custom paid-
         * download fallback in `getWixEcomOrderThankYouData` and
         * `generateOrderLineItemDownloadUrl` to the page's actions.
         */
        pageId: string;
        /**
         * Invoked exactly once when the in-modal fetch rejects (server
         * returned non-success or the call threw). Parent is responsible
         * for closing the native modal and surfacing the legacy iframe
         * thank-you modal as a last-resort fallback.
         */
        onFetchFailure: () => void;
        closeCallback: () => void;
    }
}


const DoppeViewerThankYouModalLineItemRow = reactCreateMemo(
    function DoppeViewerThankYouModalLineItemRow(props: {
        lineItem: DoppeDtoThankYouModalLineItem;
        checkoutId: string;
        pageId: string;
    }) {
        const {lineItem, checkoutId, pageId} = props;

        const viewerLocales = useDoppeViewerLocales();
        const clientServices = useDoppeViewerClientServices();

        const [downloadError, setDownloadError] = useState<string | null>(null);
        const [downloadOperation, setDownloadOperation] = useAsyncOperation<void>();
        const isDownloading = downloadOperation?.state === AsyncValueStates.Pending;

        const handleDownloadClick = useEventCallback(() => {
            setDownloadError(null);

            void setDownloadOperation((async () => {
                try {
                    const result = await clientServices.getApiClient().generateOrderLineItemDownloadUrl({
                        checkoutId: checkoutId,
                        lineItemId: lineItem.id,
                        pageId: pageId,
                    });

                    if (result.status === 'success') {
                        window.location.href = result.downloadUrl;
                        return;
                    }

                    setDownloadError(viewerLocales['thankYouModal.downloadError']);
                } catch {
                    setDownloadError(viewerLocales['thankYouModal.downloadError']);
                }
            })());
        });

        return (
            <li className={classes.lineItem}>
                {lineItem.imageUrl ?
                    <img
                        className={classes.lineItemImage}
                        src={lineItem.imageUrl}
                        alt={lineItem.imageAltText ?? ''}
                    />
                    :
                    <div className={classes.lineItemImagePlaceholder}/>
                }
                <div className={classes.lineItemDetails}>
                    <p className={classes.lineItemName}>{lineItem.name}</p>
                    <div className={classes.lineItemMeta}>
                        <span>
                            {viewerLocales['thankYouModal.lineItem.quantity']({quantity: lineItem.quantity})}
                        </span>
                        {lineItem.formattedLinePrice !== null ?
                            <span>{lineItem.formattedLinePrice}</span>
                            : null
                        }
                    </div>
                    {downloadError !== null ?
                        <p className={classes.downloadError}>{downloadError}</p>
                        : null
                    }
                </div>
                {lineItem.hasDownloadableAsset ?
                    <div className={classes.lineItemActions}>
                        <DoppeViewerHorizontalButton
                            priority={DoppeViewerHorizontalButtonPriority.Primary}
                            skin={DoppeViewerHorizontalButtonSkin.Default}
                            disabled={isDownloading}
                            onClick={handleDownloadClick}
                        >
                            {viewerLocales['thankYouModal.lineItem.downloadButton']}
                        </DoppeViewerHorizontalButton>
                    </div>
                    : null
                }
            </li>
        );
    }
);

const DoppeViewerThankYouModalPriceSummary = reactCreateMemo(
    function DoppeViewerThankYouModalPriceSummary(props: {
        thankYouData: DoppeDtoThankYouModalData;
    }) {
        const {thankYouData} = props;
        const {priceSummary} = thankYouData;

        const viewerLocales = useDoppeViewerLocales();

        return (
            <section className={classes.section}>
                <h3 className={classes.sectionTitle}>
                    {viewerLocales['thankYouModal.section.summary']}
                </h3>
                <div className={classes.priceSummary}>
                    {priceSummary.formattedSubtotal !== null ?
                        <div className={classes.priceSummaryRow}>
                            <span>{viewerLocales['thankYouModal.priceSummary.subtotal']}</span>
                            <span>{priceSummary.formattedSubtotal}</span>
                        </div>
                        : null
                    }
                    {priceSummary.formattedShipping !== null ?
                        <div className={classes.priceSummaryRow}>
                            <span>{viewerLocales['thankYouModal.priceSummary.shipping']}</span>
                            <span>{priceSummary.formattedShipping}</span>
                        </div>
                        : null
                    }
                    {priceSummary.formattedTax !== null ?
                        <div className={classes.priceSummaryRow}>
                            <span>{viewerLocales['thankYouModal.priceSummary.tax']}</span>
                            <span>{priceSummary.formattedTax}</span>
                        </div>
                        : null
                    }
                    {priceSummary.formattedDiscount !== null ?
                        <div className={classes.priceSummaryRow}>
                            <span>{viewerLocales['thankYouModal.priceSummary.discount']}</span>
                            <span>{priceSummary.formattedDiscount}</span>
                        </div>
                        : null
                    }
                    {priceSummary.formattedTotal !== null ?
                        <div className={style(classes.priceSummaryRow, {}, classes.priceSummaryRowTotal)}>
                            <span>{viewerLocales['thankYouModal.priceSummary.total']}</span>
                            <span>{priceSummary.formattedTotal}</span>
                        </div>
                        : null
                    }
                </div>
            </section>
        );
    }
);
